const enum NodeType {
  TEXT = 3,
  CDATA = 4,
  COMMENT = 8,
}
const enum NodeName {
  PLIST = 'plist',
  ARRAY = 'array',
  KEY = 'key',
  DICT = 'dict',
  STRING = 'string',
  INTEGER = 'integer',
  REAL = 'real',
  DATE = 'date',
  NULL = 'null',
  TRUE = 'true',
  FALSE = 'false',
  DATA = 'data',
}

export class PlistError extends Error {
  constructor(message: string, readonly sourceOffset: number) {
    super(message);
  }
}

/*
  based on https://github.com/TooTallNate/plist.js
*/
export class Plist {
  public warnings: PlistError[] = [];

  constructor(private options = { ignoreErrors: false }) {}

  public parse(source: string): any {
    const doc = new DOMParser().parseFromString(source, 'text/xml');
    const elementSource = doc.documentElement.outerHTML.split('\n')[0];
    const offset = source.indexOf(elementSource);

    this.invariant(
      doc.documentElement.nodeName === NodeName.PLIST,
      'Malformed document. First element should be <plist>',
      offset
    );

    const plist = this.parsePlistXML(doc.documentElement, source, offset) as any[];

    // the root <plist> node gets interpreted as an Array,
    // so pull out the inner data first
    return plist.length === 1 ? plist[0] : plist;
  }

  private parsePlistXML(node: Node, source: string, offset: number): any {
    if (!node) {
      return null;
    }

    if (node.nodeName === NodeName.PLIST || node.nodeName === NodeName.ARRAY) {
      const result = [];

      if (this.isEmptyNode(node)) {
        return result;
      }

      for (const child of node.childNodes) {
        if (this.shouldIgnoreNode(child)) {
          continue;
        }

        offset = this.findNode(source, child, offset);
        const childResult = this.parsePlistXML(child, source, offset);

        if (node.nodeName === NodeName.PLIST || null !== childResult) {
          result.push({ value: childResult, valueOffset: offset });
        }
      }

      return result;
    }

    if (node.nodeName === NodeName.DICT) {
      const result = {};
      let lastReadKey = '';
      let counter = 0;

      if (this.isEmptyNode(node)) {
        return result;
      }

      for (const child of node.childNodes) {
        offset = this.findNode(source, child, offset);

        if (this.shouldIgnoreNode(child)) {
          continue;
        }

        if (counter++ % 2 === 0) {
          this.invariant(
            child.nodeName === NodeName.KEY,
            'Missing key while parsing <dict/>.',
            offset
          );

          lastReadKey = this.parsePlistXML(child, source, offset);

          if (Object.hasOwn(result, lastReadKey)) {
            this.warnings.push(new PlistError('This value will be overwritten', result[lastReadKey].valueOffset));
          }

          result[lastReadKey] = {
            value: void 0,
            keyOffset: offset,
            valueOffset: offset,
          };

          continue;
        }

        this.invariant(
          child.nodeName !== NodeName.KEY,
          `Unexpected key "${child.nodeName}" while parsing <dict/>.`,
          offset
        );

        result[lastReadKey].valueOffset = offset;
        result[lastReadKey].value = this.parsePlistXML(child, source, offset);
      }

      return result;
    }

    if (node.nodeName === '#text') {
      // TODO: what should we do with text types? (CDATA sections)
    }

    if (node.nodeName === NodeName.KEY) {
      if (this.isEmptyNode(node)) {
        return '';
      }

      this.invariant(
        node.childNodes[0].nodeValue !== '__proto__',
        '__proto__ keys can lead to prototype pollution. More details on CVE-2022-22912',
        offset
      );

      return node.childNodes[0].nodeValue;
    }

    if (node.nodeName === NodeName.STRING) {
      let result = '';

      if (this.isEmptyNode(node)) {
        return result;
      }

      for (const child of node.childNodes) {
        const type = child.nodeType;

        if (type === NodeType.TEXT || type === NodeType.CDATA) {
          result += child.nodeValue;
        }
      }

      return result;
    }

    if (node.nodeName === NodeName.INTEGER) {
      this.invariant(
        !this.isEmptyNode(node),
        'Cannot parse "" as integer.',
        offset
      );

      const value = node.childNodes[0].nodeValue;

      this.invariant(
        isFinite(Number(value)) && !/[e+]/i.test(value),
        `Cannot parse "${value}" as integer.`,
        offset
      );

      return parseInt(value, 10);
    }

    if (node.nodeName === NodeName.REAL) {
      this.invariant(
        !this.isEmptyNode(node),
        'Cannot parse "" as real.',
        offset
      );
      let result = '';

      for (const child of node.childNodes) {
        if (child.nodeType === NodeType.TEXT) {
          result += child.nodeValue;
        }
      }

      return parseFloat(result);
    }

    if (node.nodeName === NodeName.DATE) {
      this.invariant(
        !this.isEmptyNode(node),
        'Cannot parse "" as Date.',
        offset
      );

      return new Date(node.childNodes[0].nodeValue);
    }

    if (node.nodeName === NodeName.NULL) {
      return null;
    }

    if (node.nodeName === NodeName.TRUE) {
      return true;
    }

    if (node.nodeName === NodeName.FALSE) {
      return false;
    }

    if (node.nodeName === NodeName.DATA) {
      return Array.from(node.childNodes)
        .filter((child) => child.nodeType === NodeType.TEXT)
        .reduce((result, child) => result + child.nodeValue.replace(/\s+/g, ''), '');
    }

    if (!this.options.ignoreErrors) {
      throw new PlistError(`Invalid PLIST tag ${node.nodeName}`, offset);
    }
  }

  private isEmptyNode(node) {
    return !(node.childNodes?.length > 0);
  }

  private shouldIgnoreNode(node: Node) {
    return [NodeType.TEXT, NodeType.COMMENT, NodeType.CDATA].includes(node.nodeType);
  }

  private invariant(condition: boolean, message: string, offset: number) {
    if (!condition && !this.options.ignoreErrors) {
      throw new PlistError(message, offset);
    }
  }

  private findNode(source: string, node: Node, offset: number) {
    const nodeValue = node instanceof Element ? node.outerHTML : node.nodeValue;
    let searchLine = nodeValue.split('\n')[0];

    while (searchLine.length > 0) {
      const searchOffset = source.indexOf(searchLine, offset + 1);

      if (searchOffset !== -1) {
        return searchOffset;
      }

      searchLine = searchLine.substring(0, searchLine.length - 1);
    }

    return offset;
  }
}
